'use client';

import { coords, notifyStatus } from "./coreTypes";

// Safe window access utility
export const isBrowser = () => {
  return typeof window !== 'undefined';
} 

// Popup and spinner utilities
export const safeWindow = {
  popup: (message: string, status: notifyStatus= notifyStatus.success, position?: coords) => {
    if (isBrowser() && window.popup) {
      window.popup(message, status, position);
    }
  },
  spinner: {
    show: (message: string, position?: coords) => {
      if (isBrowser() && window.spinner?.show) {
        window.spinner.show(message, position);
      }
    },
    hide: () => {
      if (isBrowser() && window.spinner?.hide) {
        window.spinner.hide();
      }
    }
  },
  location: {
    getPathname: () => isBrowser() ? window.location.pathname : '',
    getHostname: () => isBrowser() ? window.location.hostname : '',
    getHref: () => isBrowser() ? window.location.href : '',
    pushState: (state: any, title: string, url: string) => {
      if (isBrowser()) {
        window.history.pushState(state, title, url);
      }
    },
    reload: () => {
      if (isBrowser()) {
        window.location.reload();
      }
    }
  },
  audio: {
    createContext: () => {
      if (isBrowser()) {
        return new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      return null;
    }
  }
};

// Function to safely get any property from window with proper type inference
export function safeGet<T = any>(path: string, fallback: T = null as unknown as T): T {
  if (!isBrowser()) return fallback;
  
  try {
    // Handle nested properties
    const properties = path.split('.');
    let current: any = window;
    
    for (const prop of properties) {
      if (current === undefined || current === null) {
        return fallback;
      }
      current = current[prop as keyof typeof current];
    }
    
    return current !== undefined ? current : fallback;
  } catch {
    // Return fallback on any error
    return fallback;
  }
}

// Safe global instance management
export const registerGlobalInstance = (key: string, instance: any) => {
  if (isBrowser()) {
    (window as any)[key] = instance;
  }
};

// Safe document access
export const safeDocument = {
  addEventListener: (event: string, handler: EventListener) => {
    if (isBrowser()) {
      document.addEventListener(event, handler);
    }
  },
  removeEventListener: (event: string, handler: EventListener) => {
    if (isBrowser()) {
      document.removeEventListener(event, handler);
    }
  },
  getBaseURI: () => isBrowser() ? document.baseURI : '',
}; 